import { OnePlace } from "../components/OnePlace";
import { DemoVideo } from "../components/DemoVideo";
import { DemoCollectionsGrid } from "../components/DemoCollectionsGrid";
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Tag, User, Users, Baby, ArrowRight, ShieldCheck, Ruler, Clock, Heart, Store, Search, Sparkles } from "lucide-react";
import api from "../lib/apiClient";
import { Seo } from "../components/Seo";
import { Logo } from "../components/Logo";
import DealCard from "../components/DealCard";
import { Editable } from "../components/Editable";
import { useAuth } from "../context/AuthContext";
import { HIDE_SALES } from "../lib/siteConfig";

// Self-hosted, reviewed product-only flat-lay (no people/skin) — served from /public.
const HERO = "/hero-wardrobe.jpg";

const WHO = [
  { key: "all", label: "All the children", sub: "Coordinate the whole crew", icon: Users },
  { key: "boys", label: "All the boys", sub: "A matching look for the boys", icon: User },
  { key: "girls", label: "All the girls", sub: "A matching look for the girls", icon: User },
];

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [collections, setCollections] = useState([]);
  const [filter, setFilter] = useState("all");
  const collectionsRef = useRef(null);

  useEffect(() => {
    api.get("/collections").then(({ data }) => setCollections(data)).catch(() => {});
  }, []);

  // 'All the boys' hides girls-only sets, 'All the girls' hides boys-only sets, 'All the children' shows everything.
  const filtered = collections.filter((c) => {
    if (filter === "boys") return c.whom !== "girls";
    if (filter === "girls") return c.whom !== "boys";
    return true;
  }).slice(0, 3);

  // Map the Home shopping-path keys to the Match Builder's "whom" values.
  const WHOM_MAP = { all: "kids_only", boys: "boys", girls: "girls" };
  // Guests are returned to sizing after sign-in / registration (Auth reads pending_auth_return).
  const goSizing = () => { if (!user) localStorage.setItem("pending_auth_return", "/family-profile"); navigate(user ? "/family-profile" : "/auth"); };
  const pickFilter = (key) => {
    const audience = key === "boys" ? "boys" : key === "girls" ? "girls" : "all";
    navigate(`/family-deals?audience=${audience}`, { state: { whom: WHOM_MAP[key] || "kids_only" } });
  };

  return (
    <div className="space-y-14 fade-up">
      <Seo title="Coordinated family outfits & official size charts" path="/" />
      {/* Hero */}
      <section className="grid md:grid-cols-12 gap-5">
        <div className="md:col-span-7 bg-white rounded-3xl border border-line shadow-card p-8 md:p-12 flex flex-col justify-center">
          <Logo height="h-12 md:h-16" withTagline taglineClassName="text-xs md:text-sm mt-2 tracking-[0.2em]" className="self-start mb-6" />
          <Editable id="home.hero.title" as="h1" className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink">
            Your family. Our team. One matching theme.
          </Editable>
          <Editable id="home.hero.subtitle.chart-guidance" as="p" className="font-body text-base text-ink-soft mt-4 max-w-md">
            Bring your children's looks together, with a clear family budget and optional accessories. Explore five demo collections and save what your family needs.
          </Editable>
          <div className="flex flex-wrap gap-3 mt-6">
            <button onClick={() => navigate("/demo-collection")} data-testid="hero-explore-demo" className="min-h-[48px] px-6 rounded-full bg-brand text-white font-bold hover:bg-brand-hover active:scale-95 transition-all shadow-md inline-flex items-center gap-2"><Sparkles size={18} /> Explore the demo</button>
            <button onClick={goSizing} data-testid="hero-find-fit" className="min-h-[48px] px-6 rounded-full bg-white border-2 border-line text-ink font-bold hover:border-brand/40 hover:bg-blush/40 active:scale-95 transition-all inline-flex items-center gap-2"><Ruler size={18} className="text-brand" /> View size charts</button>
          </div>
          {!user && <p className="text-xs text-ink-muted mt-2" data-testid="hero-sizing-account-note">Create a free account to save optional family notes and browse size charts.</p>}
          <div className="flex flex-wrap gap-3 mt-5 text-sm">
            <span className="flex items-center gap-1.5 text-ink-soft font-semibold"><ShieldCheck size={16} className="text-gold-text" /> <Editable id="home.hero.chip1">Prepared with care</Editable></span>
            <span className="flex items-center gap-1.5 text-ink-soft font-semibold"><Ruler size={16} className="text-gold-text" /> <Editable id="home.hero.chip2.chart-guidance">Official chart guidance</Editable></span>
            <span className="flex items-center gap-1.5 text-ink-soft font-semibold"><Clock size={16} className="text-gold-text" /> <Editable id="home.hero.chip3">Coordinate within your budget</Editable></span>
          </div>
        </div>
        <div className="md:col-span-5 rounded-3xl overflow-hidden border border-line shadow-card relative min-h-[280px]">
          <img src={HERO} alt="Neatly organized family wardrobe" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/45 to-transparent" />
          <div className="absolute bottom-5 left-5 right-5 flex flex-wrap gap-2">
            <span className="bg-white/90 backdrop-blur text-ink text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1"><ShieldCheck size={13} className="text-gold-text" /> <Editable id="home.hero.badge1">Coming with our first collections</Editable></span>
            <span className="bg-white/90 backdrop-blur text-ink text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1"><Heart size={13} className="text-gold-text" /> <Editable id="home.hero.badge2">Head-to-toe coordination</Editable></span>
          </div>
        </div>
      </section>

      {/* Who are you shopping for today */}
      <section>
        <div className="text-center mb-7">
          <Editable id="home.who.title" as="h2" className="font-heading font-black text-3xl sm:text-4xl text-ink">Who are you shopping for today?</Editable>
          <Editable id="home.who.subtitle" as="p" className="text-ink-soft mt-2">Pick a starting point — you can change it any time.</Editable>
        </div>
        <div className="grid sm:grid-cols-3 gap-5">
          {WHO.map((w) => (
            <button
              key={w.key}
              onClick={() => pickFilter(w.key)}
              data-testid={`who-${w.key}`}
              className="text-left bg-white rounded-3xl border border-line shadow-soft p-7 hover:shadow-lift hover:-translate-y-1 transition-all group"
            >
              <div className="w-14 h-14 rounded-2xl bg-brand-light flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <w.icon className="text-brand" size={26} strokeWidth={2} />
              </div>
              <Editable id={`home.who.${w.key}.label`} as="h3" className="font-heading font-extrabold text-xl text-ink">{w.label}</Editable>
              <Editable id={`home.who.${w.key}.sub`} as="p" className="text-ink-soft mt-1 text-sm">{w.sub}</Editable>
              <span className="inline-flex items-center gap-1.5 text-brand font-bold mt-4 text-sm" data-testid={`who-${w.key}-cta`}>{collections.length === 0 ? "Set up a family search" : "Start matching"} <ArrowRight size={16} /></span>
            </button>
          ))}
        </div>
      </section>

      {/* Secondary services */}
      <section className={HIDE_SALES ? "" : "grid md:grid-cols-2 gap-5"}>
        {!HIDE_SALES && (
          <button
            onClick={() => navigate("/sales")}
            data-testid="choice-sales"
            className="text-left bg-white rounded-3xl border border-line shadow-soft p-8 hover:shadow-lift hover:-translate-y-1 transition-all group"
          >
            <div className="w-14 h-14 rounded-2xl bg-brand-light flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
              <Tag className="text-brand" size={26} strokeWidth={2} />
            </div>
            <h2 className="font-heading font-extrabold text-2xl text-ink"><Editable id="home.sales.title">Find Me</Editable></h2>
            <Editable id="home.sales.desc" as="p" className="text-ink-soft mt-2">See what nearby stores have on now, and we'll quietly email you when a deal matches what you're looking for.</Editable>
            <span className="inline-flex items-center gap-1.5 text-brand font-bold mt-5"><Editable id="home.sales.cta">Start browsing</Editable> <ArrowRight size={18} /></span>
          </button>
        )}

        <button
          onClick={goSizing}
          data-testid="choice-profile"
          className="w-full text-left bg-brand rounded-3xl shadow-card p-8 hover:shadow-lift hover:-translate-y-1 transition-all group text-white"
        >
          <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
            <Users className="text-white" size={26} strokeWidth={2} />
          </div>
          <h2 className="font-heading font-extrabold text-2xl"><Editable id="home.family.title">Set up your family</Editable></h2>
          <Editable id="home.family.desc" as="p" className="text-white/85 mt-2">Save optional measurements, sizes worn today, and shopping preferences. Use official charts as a reference and choose the final size yourself.</Editable>
          <span className="inline-flex items-center gap-1.5 font-bold mt-5"><Editable id="home.family.cta">Add my family</Editable> <ArrowRight size={18} /></span>
        </button>
      </section>

      <section className="space-y-6" data-testid="home-demo-experience">
        <DemoVideo />
        <DemoCollectionsGrid compact title="Then explore the five demo collections" />
      </section>
      <OnePlace testid="home-one-place" />
      {/* Featured collections — beneath the family setup so visitors immediately see what SIBLINGO produces */}
      {filtered.length > 0 && (
        <section ref={collectionsRef}>
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading font-extrabold text-2xl text-ink flex items-center gap-2"><Heart size={22} className="text-brand" /> <Editable id="home.curated.title">Curated family sets</Editable></h2>
            <button onClick={() => navigate("/family-deals")} className="text-brand font-bold text-sm flex items-center gap-1" data-testid="see-all-collections"><Editable id="home.curated.seeall">See all</Editable> <ArrowRight size={15} /></button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((c) => (
              <DealCard
                key={c.id}
                deal={c}
                canUnlock={user && !!user.is_premium}
                onGetLinks={() => navigate(`/deal/${c.id}`)}
                onPreview={() => navigate(`/deal/${c.id}`)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Looking for something else */}
      {!HIDE_SALES && (
      <section>
        <div className="rounded-3xl border border-steel-line bg-navy p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6 shadow-card">
          <div>
            <Editable id="home.market.eyebrow" as="p" className="text-xs font-bold uppercase tracking-[0.15em] text-white/60">Looking for something else?</Editable>
            <Editable id="home.market.title" as="h2" className="font-heading font-black text-2xl sm:text-3xl text-white mt-2">Search every local sale — not just clothing.</Editable>
            <Editable id="home.market.desc" as="p" className="text-white/70 mt-2 max-w-xl">Groceries, furniture, appliances, electronics, baby items, housewares, Judaica, gifts, services — or type in anything else. Pick your community and set an alert.</Editable>
          </div>
          <button
            onClick={() => navigate("/marketplace")}
            data-testid="home-marketplace-btn"
            className="shrink-0 h-14 px-7 rounded-lg bg-white text-navy font-bold text-base hover:bg-blush hover:-translate-y-0.5 active:scale-95 transition-all shadow-md flex items-center gap-2"
          >
            <Search size={20} /> <Editable id="home.market.cta">Search All Local Sales</Editable>
          </button>
        </div>
      </section>
      )}
    </div>
  );
}
